// frontend/src/components/profile/ReferralManager.js
import React, { useState, useEffect } from 'react';
import { referralsApi } from '../../api/referrals';
import { FaShareAlt, FaUserPlus, FaMoneyBillWave, FaCopy, FaSpinner, FaInfoCircle } from 'react-icons/fa';
import helpers from '../../utils/helpers';

function ReferralManager() {
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copyMessage, setCopyMessage] = useState('');

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    setLoading(true);
    try {
      // This call will automatically create the code if it doesn't exist
      const data = await referralsApi.getMyStats();
      setReferralData(data);
    } catch (err) {
      setError("Failed to load referral data.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopyMessage('Copied!');
    setTimeout(() => setCopyMessage(''), 2000);
  };

  if (loading) return <div className="text-center py-10"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading Referral Data...</div>;
  if (error) return <div className="bg-red-100 text-red-700 p-3 rounded">{error}</div>;
  if (!referralData) return <div className="text-center py-10 text-gray-500">No referral program data available.</div>;

  const referralLink = `${window.location.origin}/register?ref=${referralData.code}`;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <FaShareAlt className="text-primary" /> Referral Program
        </h2>
      </div>

      {/* --- TOOLKIT (Updated Style) --- */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8 rounded-r-lg shadow-sm flex items-start gap-3">
        <FaInfoCircle className="text-blue-500 text-xl mt-1 flex-shrink-0" />
        <div>
            <h3 className="text-sm font-bold text-blue-800">Invite a Friend, Earn Credit!</h3>
            <p className="text-blue-700 text-sm">
            Share your unique code or link. When a friend registers and makes their first purchase, you both get a reward!
            </p>
        </div>
      </div>

      {/* Referral Code Card */}
      <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200 mb-8">
        <h3 className="text-sm font-bold text-gray-500 uppercase mb-2">Your Unique Referral Code</h3>
        
        {/* UPDATED: Flex-col on mobile, Flex-row on sm+ */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-gray-100 p-3 rounded-lg border border-dashed border-gray-300">
          <span className="text-xl sm:text-2xl font-mono font-bold text-primary tracking-wider">{referralData.code}</span>
          <button 
            onClick={() => handleCopy(referralData.code)}
            className="w-full sm:w-auto bg-primary text-white px-4 py-2 rounded text-sm font-bold hover:bg-primary-dark flex items-center justify-center gap-2"
          >
            <FaCopy /> {copyMessage || 'Copy Code'}
          </button>
        </div>
        
        <h3 className="text-sm font-bold text-gray-500 uppercase mt-4 mb-2">Shareable Link</h3>
        
        {/* UPDATED: Flex-col on mobile, Flex-row on sm+ */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-gray-100 p-3 rounded-lg border border-dashed border-gray-300">
            <span className="text-sm text-gray-700 truncate w-full sm:w-auto text-center sm:text-left">{referralLink}</span>
            <button 
                onClick={() => handleCopy(referralLink)}
                className="w-full sm:w-auto text-primary text-sm font-bold hover:underline flex items-center justify-center gap-2 sm:ml-4"
            >
                <FaCopy /> Copy Link
            </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white p-5 rounded-lg shadow-sm border border-blue-200 text-center">
          <FaUserPlus className="text-blue-500 text-3xl mx-auto mb-2" />
          <p className="text-xs text-gray-500 uppercase">Total Referrals</p>
          <p className="text-2xl font-bold text-gray-800">{referralData.total_referrals}</p>
        </div>
        <div className="bg-white p-5 rounded-lg shadow-sm border border-green-200 text-center">
          <FaMoneyBillWave className="text-green-500 text-3xl mx-auto mb-2" />
          <p className="text-xs text-gray-500 uppercase">Total Earnings</p>
          <p className="text-2xl font-bold text-gray-800">{helpers.formatCurrency(referralData.total_earnings, 'NGN')}</p>
        </div>
        <div className="bg-white p-5 rounded-lg shadow-sm border border-yellow-200 text-center">
          <FaMoneyBillWave className="text-yellow-500 text-3xl mx-auto mb-2" />
          <p className="text-xs text-gray-500 uppercase">Pending Credit</p>
          <p className="text-2xl font-bold text-gray-800">{helpers.formatCurrency(0, 'NGN')}</p>
        </div>
      </div>

      {/* Referred Users List */}
      <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Referred Users ({referralData.referred_users.length})</h3>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {referralData.referred_users.length === 0 ? (
            <p className="text-gray-500 text-sm italic">No users have registered with your code yet.</p>
          ) : (
            referralData.referred_users.map((user, index) => (
              <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                <span className="font-medium text-gray-700">{user.name}</span>
                <span className="text-xs text-gray-500">Joined: {new Date(user.joined_at).toLocaleDateString()}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default ReferralManager;